import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { ContentWrapper } from './TripPlanDetailDrawer.styles'
import { useCallback, useContext, useMemo } from 'react'
import { useTripPlanDetailDrawerCopy } from './useTripPlanDetailDrawerCopy'
import TripDrawer, { type TripDrawerAnimation } from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import { useGetPlaceOverview } from 'tripPlanner/hooks/api/chat'
import { findStopById, getStopCoordinates } from 'tripPlanner/utils/chatItinerary'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import OverviewTab from './OverviewTab'
import ConfirmDeleteStopModal from './ConfirmDeleteStopModal'
import ModalContext from 'contexts/ModalContext'
import { buildThingsToDoOpenedActivityEntry } from '../utils/buildThingsToDoOpenedActivityEntry'
import { opensOnThingsToDo } from './ExperienceListingDrawer/opensOnThingsToDo'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from './TripDrawerAssistantBridge'
import { splitDrawerDetailHero } from './drawerDetailHeroLayout'
import { useDrawerDetailHeroGallery } from '../hooks/useDrawerDetailHeroGallery'
import type { MapCameraPosition, TripItineraryData } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type { ExploreItem } from 'tripPlanner/types/common'

interface Props {
  isOpen: boolean
  onDismiss: () => void
  onAfterClose?: () => void
  onBack?: () => void
  animation?: TripDrawerAnimation
  googlePlaceId: string
  placeName: string
  countryName?: string
  stopId?: number
  onDeleteStop?: (stopId: number) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  chatId?: number
}

function TripPlanDetailDrawer({
  isOpen,
  onDismiss,
  onAfterClose,
  onBack,
  animation,
  googlePlaceId,
  placeName,
  countryName,
  stopId,
  onDeleteStop,
  itineraryData,
  onSendMessage,
  chatId,
}: Props) {
  const labels = useTripPlanDetailDrawerCopy(countryName)
  const showModal = useContext(ModalContext)
  const eventBus = useTripPlannerEventBus()
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const isMobileScreen = useScreenSizeOnly('mobile')

  const { data: overview, isLoading: isOverviewLoading } = useGetPlaceOverview(
    googlePlaceId,
    chatId,
  )

  const overviewCoordinates = useMemo(() => {
    if (!overview) return undefined

    return { lat: overview.lat, lng: overview.lng }
  }, [overview])

  const showTitleInContent = shouldRenderTripDrawerTitleInContent(drawerPresentation)
  const listSubtitle = countryName && countryName !== placeName ? labels.cityInCountryText : undefined
  const titleInContent = showTitleInContent ? (overview?.locationName ?? placeName) : undefined
  const subtitleInContent = showTitleInContent ? listSubtitle : undefined

  const heroImages = useMemo((): Array<App.Image> => {
    if (!overview?.imageIds) return []

    return overview.imageIds.map((imageId): App.Image => (
      imageId.startsWith('http') ? { url: imageId } : { id: imageId }
    ))
  }, [overview?.imageIds])

  const heroGallery = useDrawerDetailHeroGallery({
    images: heroImages,
    resetKey: googlePlaceId,
    isLoading: isOverviewLoading,
    mobileFullWidth: showTitleInContent,
    viewGalleryLabel: labels.viewGalleryLabel,
  })

  const { leadingHero, inlineHero } = useMemo(
    () => splitDrawerDetailHero(showTitleInContent, heroGallery),
    [heroGallery, showTitleInContent],
  )

  const drawerHeader = useMemo(() => {
    if (showTitleInContent) return undefined

    const title = overview?.locationName ?? placeName
    if (!title) return undefined

    return <TripDrawerHeaderTitle title={title} subtitle={listSubtitle} />
  }, [listSubtitle, overview?.locationName, placeName, showTitleInContent])

  const handleDeleteStop = useCallback(async() => {
    if (stopId !== undefined && onDeleteStop) {
      const confirmed = await showModal<boolean>(
        <ConfirmDeleteStopModal placeName={placeName} />,
      )
      if (!confirmed) {
        return
      }
      onDeleteStop(stopId)
      onDismiss()
    }
  }, [stopId, onDeleteStop, onDismiss, showModal, placeName])

  const showDeleteOption = stopId !== undefined && onDeleteStop !== undefined

  const handleClose = useCallback(() => {
    onDismiss()
  }, [onDismiss])

  // Stacked, not a tab, so Back returns here. `stopId` carries the coordinate and address the
  // places search needs.
  const handleExploreThingsToDo = useCallback(() => {
    eventBus.dispatch({
      type: 'open_listing_search_drawer',
      vertical: 'experience',
      placeName,
      placeCountry: countryName,
      stopId,
    })
    if (!opensOnThingsToDo({ stopCoordinates: getStopCoordinates(findStopById(stopId, itineraryData)) })) return
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildThingsToDoOpenedActivityEntry(placeName, 'trip_planner.drawer.place_detail')],
    })
  }, [countryName, eventBus, itineraryData, placeName, stopId])

  const handleSelectPlace = useCallback((place: ExploreItem) => {
    eventBus.dispatch({
      type: 'open_place_drawer',
      googlePlaceId: place.id,
      placeName: place.name,
    })
  }, [eventBus])

  const handleEnlargeOverviewMap = useCallback((camera: MapCameraPosition) => {
    if (!overviewCoordinates) return

    handleClose()
    eventBus.dispatch({
      type: 'show_place_on_map',
      camera,
      data: {
        googlePlaceId,
        name: placeName,
        coordinates: overviewCoordinates,
      },
    })
  }, [eventBus, googlePlaceId, handleClose, overviewCoordinates, placeName])

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={onAfterClose}
      onBack={onBack}
      animation={animation}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={drawerHeader}
      onDelete={showDeleteOption ? handleDeleteStop : undefined}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={placeName}
          isOpen={isOpen}
        />
      }
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen}
        contextLabel={placeName}
      />
      <Group direction="vertical" horizontalAlign="center">
        {leadingHero}
        <ContentWrapper direction="vertical" gap={24}>
          {inlineHero}
          {(titleInContent || subtitleInContent) && (
            <Group direction="vertical" gap={4}>
              {titleInContent && (
                <Heading variant="heading3">
                  {titleInContent}
                </Heading>
              )}
              {subtitleInContent && (
                <BodyText variant="large">{subtitleInContent}</BodyText>
              )}
            </Group>
          )}

          <OverviewTab
            googlePlaceId={googlePlaceId}
            placeName={placeName}
            countryName={countryName}
            stop={findStopById(stopId, itineraryData)}
            onExploreThingsToDo={handleExploreThingsToDo}
            onEnlargeMap={isMobileScreen ? handleEnlargeOverviewMap : undefined}
            onSelectPlace={handleSelectPlace}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            chatId={chatId}
          />
        </ContentWrapper>
      </Group>
    </TripDrawer>
  )
}

export default TripPlanDetailDrawer
